<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import PublicContractsAPI from 'dashboard/api/publicContracts';
import SectionLayout from '../../account/components/SectionLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const MIN_SUMMARY_LENGTH = 6;
const INCIDENT_EVIDENCE_SOURCE = 'settings_security_public_contracts';

const severityOptions = ['minor', 'major', 'critical'];
const statusOptions = ['investigating', 'identified', 'monitoring'];

const snapshot = ref(null);
const contractHeaders = ref({});
const isLoading = ref(false);
const isSubmitting = ref(false);
const errorMessage = ref('');
const incidentError = ref('');
const permissionDenied = ref(false);

const newIncidentKey = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `dashboard-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const incidentForm = reactive({
  contract_key: '',
  severity: 'major',
  status: 'investigating',
  summary: '',
  evidence: '',
  idempotency_key: newIncidentKey(),
});

const contracts = computed(() => snapshot.value?.contracts || []);
const incidents = computed(() => snapshot.value?.incidents || []);
const summary = computed(() => snapshot.value?.summary || {});
const controls = computed(() => snapshot.value?.controls || {});

const hasSnapshot = computed(() => Boolean(snapshot.value));
const hasContracts = computed(() => contracts.value.length > 0);
const hasIncidents = computed(() => incidents.value.length > 0);

const contractNameHeader = computed(() => contractHeaders.value.name || '-');
const contractVersionHeader = computed(
  () => contractHeaders.value.version || snapshot.value?.registry_version || '-'
);

const summaryItems = computed(() => [
  {
    key: 'total',
    label: t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.SUMMARY.TOTAL'),
    value: summary.value.total_contracts || 0,
  },
  {
    key: 'documented',
    label: t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.SUMMARY.DOCUMENTED'),
    value: summary.value.documented_contracts || 0,
  },
  {
    key: 'public_links',
    label: t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.SUMMARY.PUBLIC_LINKS'),
    value: summary.value.public_links || 0,
  },
  {
    key: 'webhooks',
    label: t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.SUMMARY.WEBHOOKS'),
    value:
      (summary.value.inbound_webhooks || 0) +
      (summary.value.outbound_webhooks || 0),
  },
  {
    key: 'incidents',
    label: t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.SUMMARY.INCIDENTS'),
    value: summary.value.open_security_incidents || 0,
  },
]);

const redactionContracts = computed(() =>
  contracts.value.filter(contract => contract.controls?.includes('redaction'))
);

const adminOnlyContracts = computed(() =>
  contracts.value.filter(contract => contract.controls?.includes('admin_only'))
);

const issueContracts = computed(() =>
  contracts.value.filter(contract => contract.status !== 'ready')
);

const controlBadges = computed(() => controls.value.all_controls || []);
const redactionCoverage = computed(
  () => `${redactionContracts.value.length}/${contracts.value.length}`
);
const adminOnlyCoverage = computed(
  () => `${adminOnlyContracts.value.length}/${contracts.value.length}`
);

const canSubmitIncident = computed(
  () =>
    !isSubmitting.value &&
    !permissionDenied.value &&
    incidentForm.contract_key &&
    incidentForm.idempotency_key.trim() &&
    incidentForm.summary.trim().length >= MIN_SUMMARY_LENGTH
);

const headerValue = (headers, key) => {
  if (!headers) return '';
  if (typeof headers.get === 'function') {
    return headers.get(key) || headers.get(key.toLowerCase()) || '';
  }

  return headers[key] || headers[key.toLowerCase()] || '';
};

const statusBadgeClass = status => {
  if (status === 'healthy' || status === 'ready') {
    return 'border-n-teal-8 bg-n-teal-3 text-n-teal-11';
  }

  if (status === 'degraded' || status === 'incident_open') {
    return 'border-n-amber-8 bg-n-amber-3 text-n-amber-11';
  }

  return 'border-n-ruby-8 bg-n-ruby-3 text-n-ruby-11';
};

const statusLabel = value => {
  switch (value) {
    case 'healthy':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.STATUS.HEALTHY');
    case 'degraded':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.STATUS.DEGRADED');
    case 'unhealthy':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.STATUS.UNHEALTHY');
    case 'ready':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.STATUS.READY');
    case 'incident_open':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.STATUS.INCIDENT_OPEN');
    case 'investigating':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.STATUS.INVESTIGATING');
    case 'identified':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.STATUS.IDENTIFIED');
    case 'monitoring':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.STATUS.MONITORING');
    case 'resolved':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.STATUS.RESOLVED');
    default:
      return value || '-';
  }
};

const severityLabel = value => {
  switch (value) {
    case 'minor':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.SEVERITY.MINOR');
    case 'major':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.SEVERITY.MAJOR');
    case 'critical':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.SEVERITY.CRITICAL');
    default:
      return value || '-';
  }
};

const kindLabel = value => {
  switch (value) {
    case 'inbound_webhook':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.KIND.INBOUND_WEBHOOK');
    case 'outbound_webhook':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.KIND.OUTBOUND_WEBHOOK');
    case 'public_link':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.KIND.PUBLIC_LINK');
    case 'operational_api':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.KIND.OPERATIONAL_API');
    default:
      return value || '-';
  }
};

const controlLabel = value => {
  switch (value) {
    case 'admin_only':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.CONTROLS.ADMIN_ONLY');
    case 'hmac':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.CONTROLS.HMAC');
    case 'idempotency':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.CONTROLS.IDEMPOTENCY');
    case 'nonce':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.CONTROLS.NONCE');
    case 'opaque_token':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.CONTROLS.OPAQUE_TOKEN');
    case 'rate_limit':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.CONTROLS.RATE_LIMIT');
    case 'redaction':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.CONTROLS.REDACTION');
    case 'revocation':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.CONTROLS.REVOCATION');
    case 'ssrf':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.CONTROLS.SSRF');
    case 'timestamp':
      return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.CONTROLS.TIMESTAMP');
    default:
      return value || '-';
  }
};

const labelFor = (group, value) => {
  switch (group) {
    case 'STATUS':
      return statusLabel(value);
    case 'SEVERITY':
      return severityLabel(value);
    case 'KIND':
      return kindLabel(value);
    case 'CONTROLS':
      return controlLabel(value);
    default:
      return value || '-';
  }
};

const errorCodeMessage = value => {
  switch (value) {
    case 'idempotency_key_obrigatoria':
      return t(
        'SECURITY_SETTINGS.PUBLIC_CONTRACTS.ERROR_CODES.idempotency_key_obrigatoria'
      );
    case 'contract_key_obrigatorio':
      return t(
        'SECURITY_SETTINGS.PUBLIC_CONTRACTS.ERROR_CODES.contract_key_obrigatorio'
      );
    case 'contract_key_invalido':
      return t(
        'SECURITY_SETTINGS.PUBLIC_CONTRACTS.ERROR_CODES.contract_key_invalido'
      );
    case 'severity_invalida':
      return t(
        'SECURITY_SETTINGS.PUBLIC_CONTRACTS.ERROR_CODES.severity_invalida'
      );
    case 'status_invalido':
      return t(
        'SECURITY_SETTINGS.PUBLIC_CONTRACTS.ERROR_CODES.status_invalido'
      );
    default:
      return '';
  }
};

const formatDate = value => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const applySnapshotResponse = response => {
  snapshot.value = response?.data?.payload || null;
  contractHeaders.value = {
    name: headerValue(response?.headers, 'X-Lais-Contract-Name'),
    version: headerValue(response?.headers, 'X-Lais-Contract-Version'),
  };

  if (!incidentForm.contract_key && contracts.value.length) {
    incidentForm.contract_key = contracts.value[0].key;
  }
};

const errorFromResponse = (error, fallbackMessage) => {
  const status = error?.response?.status;
  if (status === 401 || status === 403) {
    permissionDenied.value = true;
    return t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.PERMISSION_DENIED');
  }

  const backendMessage = error?.response?.data?.message;
  const translatedBackendMessage = errorCodeMessage(backendMessage);
  if (translatedBackendMessage) return translatedBackendMessage;
  if (backendMessage) return backendMessage;

  return fallbackMessage;
};

const loadStatus = async () => {
  try {
    isLoading.value = true;
    errorMessage.value = '';
    permissionDenied.value = false;
    const response = await PublicContractsAPI.securityStatus();
    applySnapshotResponse(response);
  } catch (error) {
    errorMessage.value = errorFromResponse(
      error,
      t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.LOAD_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};

const incidentPayload = () => ({
  contract_key: incidentForm.contract_key,
  severity: incidentForm.severity,
  status: incidentForm.status,
  summary: incidentForm.summary.trim(),
  idempotency_key: incidentForm.idempotency_key.trim(),
  evidence: {
    source: INCIDENT_EVIDENCE_SOURCE,
    operator_note: incidentForm.evidence.trim() || undefined,
    snapshot_generated_at: snapshot.value?.generated_at,
    registry_version: snapshot.value?.registry_version,
  },
});

const resetIncidentForm = () => {
  incidentForm.summary = '';
  incidentForm.evidence = '';
  incidentForm.idempotency_key = newIncidentKey();
};

const createIncident = async () => {
  if (!canSubmitIncident.value) return;

  try {
    isSubmitting.value = true;
    incidentError.value = '';
    const response =
      await PublicContractsAPI.createSecurityIncident(incidentPayload());
    const created = response?.data?.payload?.created;
    useAlert(
      created
        ? t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.INCIDENT.SUCCESS_CREATED')
        : t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.INCIDENT.SUCCESS_REUSED')
    );
    resetIncidentForm();
    await loadStatus();
  } catch (error) {
    incidentError.value = errorFromResponse(
      error,
      t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.INCIDENT.ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(loadStatus);
</script>

<template>
  <SectionLayout
    :title="t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.TITLE')"
    :description="t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.DESCRIPTION')"
    with-border
  >
    <template #headerActions>
      <Button
        v-tooltip.top="t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.REFRESH')"
        icon="i-lucide-refresh-cw"
        color="slate"
        variant="ghost"
        size="sm"
        :is-loading="isLoading"
        @click="loadStatus"
      />
    </template>

    <div class="grid gap-4">
      <div
        v-if="isLoading && !hasSnapshot"
        class="rounded-md border border-n-weak bg-n-alpha-1 px-4 py-8 text-center text-sm text-n-slate-11"
      >
        {{ t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.LOADING') }}
      </div>

      <div
        v-else-if="errorMessage"
        class="rounded-md border border-n-ruby-8 bg-n-ruby-3 px-4 py-3 text-sm text-n-ruby-11"
        data-testid="public-contracts-error"
      >
        {{ errorMessage }}
      </div>

      <template v-else-if="hasSnapshot">
        <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <div
            v-for="item in summaryItems"
            :key="item.key"
            class="rounded-md border border-n-weak bg-n-alpha-1 px-3 py-3"
          >
            <div class="text-xs font-medium uppercase text-n-slate-10">
              {{ item.label }}
            </div>
            <div class="mt-1 text-2xl font-semibold text-n-slate-12">
              {{ item.value }}
            </div>
          </div>
        </div>

        <div
          class="grid gap-3 rounded-md border border-n-weak bg-n-alpha-1 p-4 lg:grid-cols-3"
        >
          <div>
            <div class="text-xs font-medium uppercase text-n-slate-10">
              {{ t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.HEADERS.NAME') }}
            </div>
            <div class="mt-1 break-all text-sm font-medium text-n-slate-12">
              {{ contractNameHeader }}
            </div>
          </div>
          <div>
            <div class="text-xs font-medium uppercase text-n-slate-10">
              {{ t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.HEADERS.VERSION') }}
            </div>
            <div class="mt-1 break-all text-sm font-medium text-n-slate-12">
              {{ contractVersionHeader }}
            </div>
          </div>
          <div>
            <div class="text-xs font-medium uppercase text-n-slate-10">
              {{ t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.HEADERS.GENERATED_AT') }}
            </div>
            <div class="mt-1 text-sm font-medium text-n-slate-12">
              {{ formatDate(snapshot.generated_at) }}
            </div>
          </div>
        </div>

        <div
          class="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(320px,420px)]"
        >
          <div class="grid gap-4">
            <div class="rounded-md border border-n-weak bg-n-alpha-1 p-4">
              <div
                class="flex flex-col gap-2 border-b border-n-weak pb-3 md:flex-row md:items-start md:justify-between"
              >
                <div>
                  <h4 class="text-base font-semibold text-n-slate-12">
                    {{ t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.POSTURE.TITLE') }}
                  </h4>
                  <p class="mt-1 text-sm text-n-slate-11">
                    {{
                      t(
                        'SECURITY_SETTINGS.PUBLIC_CONTRACTS.POSTURE.DESCRIPTION'
                      )
                    }}
                  </p>
                </div>
                <span
                  class="inline-flex w-fit items-center rounded-md border px-2 py-1 text-xs font-medium"
                  :class="statusBadgeClass(snapshot.status)"
                  data-testid="public-contracts-overall-status"
                >
                  {{ labelFor('STATUS', snapshot.status) }}
                </span>
              </div>

              <div class="mt-3 grid gap-3 sm:grid-cols-2">
                <div>
                  <div class="text-xs font-medium uppercase text-n-slate-10">
                    {{
                      t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.POSTURE.REDACTION')
                    }}
                  </div>
                  <div class="mt-1 text-sm font-semibold text-n-slate-12">
                    {{ redactionCoverage }}
                  </div>
                </div>
                <div>
                  <div class="text-xs font-medium uppercase text-n-slate-10">
                    {{
                      t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.POSTURE.ADMIN_ONLY')
                    }}
                  </div>
                  <div class="mt-1 text-sm font-semibold text-n-slate-12">
                    {{ adminOnlyCoverage }}
                  </div>
                </div>
              </div>

              <div class="mt-3 flex flex-wrap gap-2">
                <span
                  v-for="control in controlBadges"
                  :key="control"
                  class="inline-flex items-center rounded-md border border-n-weak bg-white px-2 py-1 text-xs text-n-slate-11"
                >
                  {{ labelFor('CONTROLS', control) }}
                </span>
                <span
                  v-if="controlBadges.length === 0"
                  class="text-sm text-n-slate-11"
                >
                  {{
                    t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.POSTURE.NO_CONTROLS')
                  }}
                </span>
              </div>
            </div>

            <div class="rounded-md border border-n-weak bg-n-alpha-1 p-4">
              <h4 class="text-base font-semibold text-n-slate-12">
                {{ t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.ISSUES.TITLE') }}
              </h4>
              <div
                v-if="!hasIncidents && issueContracts.length === 0"
                class="mt-3 rounded-md border border-n-weak bg-white px-3 py-3 text-sm text-n-slate-11"
                data-testid="public-contracts-empty-issues"
              >
                {{ t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.ISSUES.EMPTY') }}
              </div>
              <div v-else class="mt-3 grid gap-3">
                <div
                  v-for="contract in issueContracts"
                  :key="contract.key"
                  class="rounded-md border border-n-weak bg-white px-3 py-3"
                >
                  <div
                    class="flex flex-wrap items-center justify-between gap-2"
                  >
                    <div class="min-w-0">
                      <div
                        class="break-all text-sm font-semibold text-n-slate-12"
                      >
                        {{ contract.name }}
                      </div>
                      <div class="break-all text-xs text-n-slate-10">
                        {{ contract.key }}
                      </div>
                    </div>
                    <span
                      class="inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium"
                      :class="statusBadgeClass(contract.status)"
                    >
                      {{ labelFor('STATUS', contract.status) }}
                    </span>
                  </div>
                </div>

                <div
                  v-for="incident in incidents"
                  :key="incident.id || incident.slug"
                  class="rounded-md border border-n-weak bg-white px-3 py-3"
                  data-testid="public-contract-incident-row"
                >
                  <div
                    class="flex flex-wrap items-center justify-between gap-2"
                  >
                    <div class="min-w-0">
                      <div
                        class="break-all text-sm font-semibold text-n-slate-12"
                      >
                        {{ incident.contract_key }}
                      </div>
                      <div class="break-all text-xs text-n-slate-10">
                        {{ incident.slug || `#${incident.id}` }}
                      </div>
                    </div>
                    <span
                      class="inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium"
                      :class="statusBadgeClass(incident.severity)"
                    >
                      {{ labelFor('SEVERITY', incident.severity) }}
                    </span>
                  </div>
                  <div
                    class="mt-2 grid gap-1 text-xs text-n-slate-11 sm:grid-cols-2"
                  >
                    <span>{{ labelFor('STATUS', incident.status) }}</span>
                    <span>{{ formatDate(incident.updated_at) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <form
            class="rounded-md border border-n-weak bg-n-alpha-1 p-4"
            data-testid="public-contract-incident-form"
            @submit.prevent="createIncident"
          >
            <h4 class="text-base font-semibold text-n-slate-12">
              {{ t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.INCIDENT.TITLE') }}
            </h4>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.INCIDENT.DESCRIPTION') }}
            </p>

            <div
              v-if="incidentError"
              class="mt-3 rounded-md border border-n-ruby-8 bg-n-ruby-3 px-3 py-2 text-sm text-n-ruby-11"
              data-testid="public-contract-incident-error"
            >
              {{ incidentError }}
            </div>

            <div class="mt-4 grid gap-3">
              <label class="grid gap-1 text-sm font-medium text-n-slate-12">
                {{ t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.INCIDENT.CONTRACT') }}
                <select
                  v-model="incidentForm.contract_key"
                  class="h-10 rounded-md border border-n-weak bg-white px-3 text-sm text-n-slate-12"
                  :disabled="!hasContracts || permissionDenied"
                  data-testid="public-contract-incident-contract"
                >
                  <option
                    v-for="contract in contracts"
                    :key="contract.key"
                    :value="contract.key"
                  >
                    {{ contract.name }}
                  </option>
                </select>
              </label>

              <div class="grid gap-3 sm:grid-cols-2">
                <label class="grid gap-1 text-sm font-medium text-n-slate-12">
                  {{
                    t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.INCIDENT.SEVERITY')
                  }}
                  <select
                    v-model="incidentForm.severity"
                    class="h-10 rounded-md border border-n-weak bg-white px-3 text-sm text-n-slate-12"
                  >
                    <option
                      v-for="severity in severityOptions"
                      :key="severity"
                      :value="severity"
                    >
                      {{ labelFor('SEVERITY', severity) }}
                    </option>
                  </select>
                </label>

                <label class="grid gap-1 text-sm font-medium text-n-slate-12">
                  {{ t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.INCIDENT.STATUS') }}
                  <select
                    v-model="incidentForm.status"
                    class="h-10 rounded-md border border-n-weak bg-white px-3 text-sm text-n-slate-12"
                  >
                    <option
                      v-for="status in statusOptions"
                      :key="status"
                      :value="status"
                    >
                      {{ labelFor('STATUS', status) }}
                    </option>
                  </select>
                </label>
              </div>

              <label class="grid gap-1 text-sm font-medium text-n-slate-12">
                {{
                  t(
                    'SECURITY_SETTINGS.PUBLIC_CONTRACTS.INCIDENT.IDEMPOTENCY_KEY'
                  )
                }}
                <input
                  v-model="incidentForm.idempotency_key"
                  class="h-10 rounded-md border border-n-weak bg-white px-3 text-sm text-n-slate-12"
                  type="text"
                  data-testid="public-contract-incident-key"
                />
              </label>

              <label class="grid gap-1 text-sm font-medium text-n-slate-12">
                {{ t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.INCIDENT.SUMMARY') }}
                <textarea
                  v-model="incidentForm.summary"
                  class="min-h-[88px] resize-y rounded-md border border-n-weak bg-white px-3 py-2 text-sm text-n-slate-12"
                  data-testid="public-contract-incident-summary"
                />
              </label>

              <label class="grid gap-1 text-sm font-medium text-n-slate-12">
                {{ t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.INCIDENT.EVIDENCE') }}
                <textarea
                  v-model="incidentForm.evidence"
                  class="min-h-[72px] resize-y rounded-md border border-n-weak bg-white px-3 py-2 text-sm text-n-slate-12"
                  data-testid="public-contract-incident-evidence"
                />
              </label>
            </div>

            <div class="mt-4 flex flex-wrap items-center justify-end gap-2">
              <Button
                icon="i-lucide-refresh-cw"
                color="slate"
                variant="ghost"
                size="sm"
                :label="
                  t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.INCIDENT.NEW_KEY')
                "
                type="button"
                @click="incidentForm.idempotency_key = newIncidentKey()"
              />
              <Button
                icon="i-lucide-shield-alert"
                color="ruby"
                size="sm"
                type="submit"
                :disabled="!canSubmitIncident"
                :is-loading="isSubmitting"
                :label="t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.INCIDENT.SUBMIT')"
                data-testid="public-contract-incident-submit"
              />
            </div>
          </form>
        </div>

        <div
          v-if="!hasContracts"
          class="rounded-md border border-n-weak bg-n-alpha-1 px-4 py-8 text-center text-sm text-n-slate-11"
          data-testid="public-contracts-empty"
        >
          {{ t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.EMPTY') }}
        </div>

        <div v-else class="overflow-hidden rounded-md border border-n-weak">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-n-weak text-sm">
              <thead
                class="bg-n-alpha-1 text-left text-xs uppercase text-n-slate-10"
              >
                <tr>
                  <th class="px-3 py-2 font-medium">
                    {{ t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.TABLE.CONTRACT') }}
                  </th>
                  <th class="px-3 py-2 font-medium">
                    {{ t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.TABLE.KIND') }}
                  </th>
                  <th class="px-3 py-2 font-medium">
                    {{ t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.TABLE.VERSION') }}
                  </th>
                  <th class="px-3 py-2 font-medium">
                    {{ t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.TABLE.STATUS') }}
                  </th>
                  <th class="px-3 py-2 font-medium">
                    {{ t('SECURITY_SETTINGS.PUBLIC_CONTRACTS.TABLE.CONTROLS') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak bg-white">
                <tr v-for="contract in contracts" :key="contract.key">
                  <td class="px-3 py-3 align-top">
                    <div class="break-all font-medium text-n-slate-12">
                      {{ contract.name }}
                    </div>
                    <div class="break-all text-xs text-n-slate-10">
                      {{ contract.key }}
                    </div>
                  </td>
                  <td class="px-3 py-3 align-top text-n-slate-11">
                    {{ labelFor('KIND', contract.kind) }}
                  </td>
                  <td class="break-all px-3 py-3 align-top text-n-slate-11">
                    {{ contract.version }}
                  </td>
                  <td class="px-3 py-3 align-top">
                    <span
                      class="inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium"
                      :class="statusBadgeClass(contract.status)"
                    >
                      {{ labelFor('STATUS', contract.status) }}
                    </span>
                  </td>
                  <td class="px-3 py-3 align-top">
                    <div class="flex max-w-[360px] flex-wrap gap-1">
                      <span
                        v-for="control in contract.controls"
                        :key="`${contract.key}-${control}`"
                        class="inline-flex rounded-md border border-n-weak bg-n-alpha-1 px-2 py-1 text-xs text-n-slate-11"
                      >
                        {{ labelFor('CONTROLS', control) }}
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </div>
  </SectionLayout>
</template>
